import { cacheLife, cacheTag } from "next/cache"
import { wordPressApi } from "@/services/api"
import {
  buildAllowedAssetOriginSet,
  extractInlineStyles,
  extractRenderableMarkup,
  extractScripts,
  extractStylesheets,
  isAllowedAssetUrl,
} from "@/services/static-page-service/utils"

export interface StaticPageRenderedField {
  rendered?: string
}

export interface StaticPage {
  id: number
  slug: string
  link?: string
  status?: string
  title?: StaticPageRenderedField
  content?: StaticPageRenderedField
}

export type StaticPageRenderMode = "full" | "fallback"

export interface StaticPageScriptDescriptor {
  src?: string
  inlineCode?: string
  type?: string
  async?: boolean
  defer?: boolean
}

export interface StaticPageRenderPayload {
  page: StaticPage
  html: string | null
  stylesheets: string[]
  inlineStyles: string[]
  scripts: StaticPageScriptDescriptor[]
  mode: StaticPageRenderMode
  pageUrl: string | null
  baseUrl: string | null
}

const getBaseUrl = (pageUrl: string | null): string | null => {
  if (!pageUrl) {
    return null
  }

  try {
    return new URL(pageUrl).origin
  } catch {
    return null
  }
}

const WP_PAGES_CACHE_TAG = "wp-pages"
const WP_PAGE_FALLBACK_CACHE_LIFE = "minutes"
const SCRIPT_TAG_REGEX = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi
const SCRIPT_SRC_ATTRIBUTE_REGEX =
  /\bsrc\s*=\s*(?:"([^"]+)"|'([^']+)'|([^\s"'=<>`]+))/i

const getScriptSourceFromAttributes = (attributes: string): string | null => {
  const sourceMatch = attributes.match(SCRIPT_SRC_ATTRIBUTE_REGEX)
  const source = sourceMatch?.[1] ?? sourceMatch?.[2] ?? sourceMatch?.[3]

  return source ?? null
}

const resolveAssetUrl = (value: string, pageUrl: string): string | null => {
  try {
    return new URL(value, pageUrl).toString()
  } catch {
    return null
  }
}

const hasScriptParityDegradation = (
  fullHtml: string,
  pageUrl: string
): boolean => {
  const allowedOrigins = buildAllowedAssetOriginSet(pageUrl)

  for (const match of fullHtml.matchAll(SCRIPT_TAG_REGEX)) {
    const attributes = match[1] ?? ""
    const src = getScriptSourceFromAttributes(attributes)

    if (!src) {
      continue
    }

    const resolvedSrc = resolveAssetUrl(src, pageUrl)

    if (!(resolvedSrc && isAllowedAssetUrl(resolvedSrc, allowedOrigins))) {
      return true
    }
  }

  return false
}

const getStaticPageCacheTagBySlug = (slug: string): string | null => {
  const normalizedSlug = slug.trim().toLowerCase()

  if (!normalizedSlug) {
    return null
  }

  return `wp-page:${normalizedSlug}`
}

const buildStaticPageRenderPayload = ({
  page,
  html,
  stylesheets,
  inlineStyles,
  scripts,
  mode,
  pageUrl,
  baseUrl,
}: {
  page: StaticPage
  html: string | null
  stylesheets: string[]
  inlineStyles: string[]
  scripts: StaticPageScriptDescriptor[]
  mode: StaticPageRenderMode
  pageUrl: string | null
  baseUrl: string | null
}): StaticPageRenderPayload => ({
  page,
  html,
  stylesheets,
  inlineStyles,
  scripts,
  mode,
  pageUrl,
  baseUrl,
})

export async function getStaticPageBySlug(
  slug: string
): Promise<StaticPage | null> {
  "use cache"

  cacheLife("hours")
  cacheTag(WP_PAGES_CACHE_TAG)

  const pageCacheTag = getStaticPageCacheTagBySlug(slug)

  if (pageCacheTag) {
    cacheTag(pageCacheTag)
  }

  const { data } = await wordPressApi.get<StaticPage[]>("/wp/v2/pages", {
    params: {
      slug,
      _fields: "id,slug,link,status,title,content",
      status: "publish",
      per_page: 1,
    },
  })

  if (!Array.isArray(data) || data.length === 0) {
    return null
  }

  return data[0] ?? null
}

const WP_PAGES_SLUGS_PER_PAGE = 100
const WP_PAGES_SLUGS_MAX_PAGES = 100

const parseWpTotalPagesHeader = (headerValue: unknown): number | null => {
  const normalizedHeaderValue = Array.isArray(headerValue)
    ? headerValue[0]
    : headerValue

  if (typeof normalizedHeaderValue !== "string") {
    return null
  }

  const parsedTotalPages = Number.parseInt(normalizedHeaderValue, 10)

  if (!Number.isFinite(parsedTotalPages) || parsedTotalPages < 1) {
    return null
  }

  return parsedTotalPages
}

export async function getPublishedStaticPageSlugs(): Promise<string[]> {
  "use cache"

  cacheLife("hours")
  cacheTag(WP_PAGES_CACHE_TAG)

  const slugs: string[] = []
  const seenSlugs = new Set<string>()
  let currentPage = 1
  let totalPages: number | null = null

  while (currentPage <= WP_PAGES_SLUGS_MAX_PAGES) {
    const { data, headers } = await wordPressApi.get<
      Pick<StaticPage, "slug">[]
    >("/wp/v2/pages", {
      params: {
        _fields: "slug",
        status: "publish",
        per_page: WP_PAGES_SLUGS_PER_PAGE,
        page: currentPage,
      },
    })

    if (!Array.isArray(data) || data.length === 0) {
      break
    }

    const parsedTotalPages = parseWpTotalPagesHeader(
      headers?.["x-wp-totalpages"]
    )

    if (parsedTotalPages) {
      totalPages = parsedTotalPages
    }

    for (const page of data) {
      const slug = page.slug?.trim()

      if (!slug || seenSlugs.has(slug)) {
        continue
      }

      seenSlugs.add(slug)
      slugs.push(slug)
    }

    if (data.length < WP_PAGES_SLUGS_PER_PAGE) {
      break
    }

    if (totalPages && currentPage >= totalPages) {
      break
    }

    currentPage += 1
  }

  return slugs
}

export async function getStaticPageFullHtml(
  pageUrl: string
): Promise<string | null> {
  try {
    const response = await fetch(pageUrl)

    if (!response.ok) {
      return null
    }

    return await response.text()
  } catch {
    return null
  }
}

export async function getStaticPageRenderPayloadBySlug(
  slug: string
): Promise<StaticPageRenderPayload | null> {
  "use cache"

  cacheLife("hours")
  cacheTag(WP_PAGES_CACHE_TAG)

  const pageCacheTag = getStaticPageCacheTagBySlug(slug)

  if (pageCacheTag) {
    cacheTag(pageCacheTag)
  }

  const page = await getStaticPageBySlug(slug)

  if (!page) {
    return null
  }

  const pageUrl = page.link
  const baseUrl = getBaseUrl(pageUrl ?? null)
  const fullHtml = pageUrl ? await getStaticPageFullHtml(pageUrl) : null

  if (!(pageUrl && fullHtml)) {
    cacheLife(WP_PAGE_FALLBACK_CACHE_LIFE)

    return buildStaticPageRenderPayload({
      page,
      html: page.content?.rendered ?? null,
      stylesheets: [],
      inlineStyles: [],
      scripts: [],
      mode: "fallback",
      pageUrl: pageUrl ?? null,
      baseUrl,
    })
  }

  const extractedHtml = extractRenderableMarkup(fullHtml)
  const extractedScripts = extractScripts(fullHtml, pageUrl)
  const isScriptParityDegraded = hasScriptParityDegradation(fullHtml, pageUrl)
  const mode: StaticPageRenderMode =
    extractedHtml && !isScriptParityDegraded ? "full" : "fallback"

  if (mode === "fallback") {
    cacheLife(WP_PAGE_FALLBACK_CACHE_LIFE)
  }

  return buildStaticPageRenderPayload({
    page,
    html: mode === "full" ? extractedHtml : (page.content?.rendered ?? null),
    stylesheets: extractStylesheets(fullHtml, pageUrl),
    inlineStyles: extractInlineStyles(fullHtml),
    scripts: mode === "full" ? extractedScripts : [],
    mode,
    pageUrl,
    baseUrl,
  })
}
